import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Users, Plus, Trophy, X, Sparkles } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { buildAvatarUrl, defaultAvatar } from "@/lib/avatar";

const TAG_COLORS = ["2EE38A", "00F0FF", "3E7A5C", "FF6B00", "39FF14", "FFD600"];

export default function Crews() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [crews, setCrews] = useState([]);
  const [myCrew, setMyCrew] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: list }, me] = await Promise.all([
        api.get("/crews?sort=leaderboard"),
        user ? api.get("/crews/me") : Promise.resolve({ data: { crew: null } }),
      ]);
      setCrews(list);
      setMyCrew(me.data.crew);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load crews");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="min-h-[calc(100vh-4rem)] relative" data-testid="crews-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div>
            <div className="label-mono text-brand-magenta">// crews</div>
            <h1 className="font-display font-extrabold text-4xl sm:text-6xl tracking-tighter mt-2">
              Form a crew. <span className="gradient-text">Climb the ranks.</span>
            </h1>
            <p className="text-white/60 mt-3 max-w-xl">
              A crew = your squad. Pick your tag, invite your friends, and race to the top of the leaderboard.
            </p>
          </div>
          <div className="flex gap-3">
            {myCrew ? (
              <Link to={`/crews/${myCrew.crew_id}`} className="btn-primary" data-testid="my-crew-btn">
                <Users className="w-4 h-4" /> My Crew [{myCrew.tag}]
              </Link>
            ) : (
              user && (
                <button onClick={() => setShowCreate(true)} className="btn-primary" data-testid="create-crew-btn">
                  <Plus className="w-4 h-4" /> Form Crew
                </button>
              )
            )}
          </div>
        </div>

        {!user && (
          <div className="card-glass p-6 mb-8 flex flex-col sm:flex-row items-center justify-between gap-4" data-testid="crews-login-prompt">
            <div>
              <div className="font-display font-bold text-white text-lg">Sign in first</div>
              <div className="text-white/60 text-sm">You need an account to create or join a crew.</div>
            </div>
            <Link to="/login" className="btn-primary">Sign in →</Link>
          </div>
        )}

        {/* Top 3 podium */}
        {!loading && crews.length >= 3 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
            {crews.slice(0, 3).map((c, i) => (
              <PodiumCard key={c.crew_id} crew={c} rank={i + 1} />
            ))}
          </div>
        )}

        {/* List */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {loading
            ? Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="card-glass p-6 h-44 animate-pulse-soft" />
              ))
            : crews.length === 0
            ? (
              <div className="col-span-full card-glass p-10 text-center" data-testid="crews-empty">
                <Users className="w-10 h-10 mx-auto text-white/30 mb-3" />
                <div className="text-white/70">No crews yet. Be the first to form one.</div>
              </div>
            )
            : crews.map((c) => <CrewCard key={c.crew_id} crew={c} />)}
        </div>
      </div>

      {showCreate && (
        <CreateCrewModal
          onClose={() => setShowCreate(false)}
          onCreated={(newCrew) => {
            setShowCreate(false);
            if (newCrew?.crew_id) {
              nav(`/crews/${newCrew.crew_id}`);
            } else {
              load();
            }
          }}
        />
      )}
    </div>
  );
}

function PodiumCard({ crew, rank }) {
  const medals = { 1: "🥇", 2: "🥈", 3: "🥉" };
  const bgs = {
    1: "from-brand-amber/40 to-brand-magenta/30 border-brand-amber/40",
    2: "from-white/20 to-white/5 border-white/20",
    3: "from-brand-violet/40 to-brand-magenta/20 border-brand-violet/40",
  };
  return (
    <Link
      to={`/crews/${crew.crew_id}`}
      className={`relative rounded-3xl border bg-gradient-to-br ${bgs[rank]} p-6 hover:scale-[1.02] transition-transform`}
      data-testid={`podium-${rank}`}
    >
      <div className="absolute top-4 right-4 text-3xl">{medals[rank]}</div>
      <div className="flex items-center gap-3">
        <div
          className="w-14 h-14 rounded-2xl grid place-items-center font-display font-extrabold text-white text-lg shadow-glow-magenta"
          style={{ background: `linear-gradient(135deg, #${crew.color}, #3E7A5C)` }}
        >
          {crew.tag.slice(0, 2)}
        </div>
        <div>
          <div className="font-display font-bold text-xl text-white">{crew.name}</div>
          <div className="text-xs font-mono text-white/60">[{crew.tag}] · {crew.member_count} members</div>
        </div>
      </div>
      <div className="mt-5 flex items-end justify-between">
        <div>
          <div className="label-mono">total winnings</div>
          <div className="font-mono font-bold text-2xl text-brand-lime">{crew.total_won.toFixed(0)} <span className="text-xs text-white/50">USDT</span></div>
        </div>
        <Trophy className="w-6 h-6 text-brand-amber" />
      </div>
    </Link>
  );
}

function CrewCard({ crew }) {
  return (
    <Link
      to={`/crews/${crew.crew_id}`}
      className="card-glass p-5 hover:-translate-y-1 transition-transform group"
      data-testid={`crew-${crew.crew_id}`}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-12 h-12 rounded-2xl grid place-items-center font-display font-extrabold text-white shadow-glow-violet shrink-0"
          style={{ background: `linear-gradient(135deg, #${crew.color}, #00F0FF)` }}
        >
          {crew.tag.slice(0, 2)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-white truncate">{crew.name}</div>
          <div className="text-xs font-mono text-white/50">[{crew.tag}]</div>
          {crew.motto && <div className="text-xs text-white/60 mt-1.5 line-clamp-2">{crew.motto}</div>}
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between">
        <div className="flex -space-x-2">
          {crew.members.slice(0, 5).map((m) => (
            <img
              key={m.user_id}
              src={buildAvatarUrl(m.avatar || defaultAvatar(m.user_id), 60)}
              alt={m.name}
              className="w-7 h-7 rounded-full border-2 border-ink-800 bg-ink-700"
            />
          ))}
          {crew.member_count > 5 && (
            <div className="w-7 h-7 rounded-full border-2 border-ink-800 bg-ink-700 grid place-items-center text-[10px] font-mono text-white/70">
              +{crew.member_count - 5}
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="text-xs font-mono text-white/50">earned</div>
          <div className="font-mono font-bold text-brand-lime">{crew.total_won.toFixed(0)} USDT</div>
        </div>
      </div>
    </Link>
  );
}

function CreateCrewModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [tag, setTag] = useState("");
  const [motto, setMotto] = useState("");
  const [color, setColor] = useState(TAG_COLORS[0]);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await api.post("/crews", { name, tag, motto, color });
      toast.success("Crew created! 🎉");
      onCreated(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose} data-testid="create-crew-modal">
      <motion.form
        initial={{ opacity: 0, y: 20, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="card-glass p-7 w-full max-w-lg"
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="label-mono">// new crew</div>
            <div className="font-display font-extrabold text-2xl mt-1">Form a Crew</div>
          </div>
          <button type="button" onClick={onClose} className="btn-ghost !p-2" data-testid="modal-close-btn">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Preview */}
        <div className="flex items-center gap-3 p-4 rounded-2xl bg-white/5 mb-5">
          <div
            className="w-14 h-14 rounded-2xl grid place-items-center font-display font-extrabold text-white shadow-glow-magenta"
            style={{ background: `linear-gradient(135deg, #${color}, #3E7A5C)` }}
          >
            {(tag || "NX").toUpperCase().slice(0, 2)}
          </div>
          <div>
            <div className="font-display font-bold text-white">{name || "Your crew name"}</div>
            <div className="text-xs font-mono text-white/50">[{(tag || "TAG").toUpperCase()}]</div>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="label-mono mb-2 block">Crew name (2–24)</label>
            <input
              type="text"
              required
              minLength={2}
              maxLength={24}
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="field"
              placeholder="Neon Lords"
              data-testid="crew-name-input"
            />
          </div>
          <div>
            <label className="label-mono mb-2 block">Tag (2–5 chars, unique)</label>
            <input
              type="text"
              required
              minLength={2}
              maxLength={5}
              value={tag}
              onChange={(e) => setTag(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
              className="field font-mono uppercase"
              placeholder="NEON"
              data-testid="crew-tag-input"
            />
          </div>
          <div>
            <label className="label-mono mb-2 block">Motto (optional)</label>
            <input
              type="text"
              maxLength={80}
              value={motto}
              onChange={(e) => setMotto(e.target.value)}
              className="field"
              placeholder="Don't go solo."
              data-testid="crew-motto-input"
            />
          </div>
          <div>
            <label className="label-mono mb-2 block">Color</label>
            <div className="flex gap-2">
              {TAG_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={`w-10 h-10 rounded-xl border-2 transition-all ${
                    color === c ? "border-white scale-110" : "border-white/20 hover:border-white/50"
                  }`}
                  style={{ background: `#${c}` }}
                  data-testid={`color-${c}`}
                />
              ))}
            </div>
          </div>
        </div>

        <button type="submit" disabled={submitting} className="btn-primary w-full mt-7" data-testid="crew-submit-btn">
          <Sparkles className="w-4 h-4" /> {submitting ? "Creating..." : "Create crew"}
        </button>
      </motion.form>
    </div>
  );
}
